/**
 * Извлекает элементы и связи модели для отображения в дереве навигатора.
 * Совместим с moddle-моделью ArchiMate (ArchiX6).
 */
import {
	AND_JUNCTION,
	OR_JUNCTION,
	getLayerType,
	getSemanticType,
	getTypeName,
} from '@core_x6/lib/util/ModelUtil';

export type ModelConcept = {id: string; name: string; type: string};

export type ModelContent = {
	/** Элементы и связи по папкам (strategy, business, ..., relations) */
	elementsByFolder: Record<string, ModelConcept[]>;
};

/**
 * Папка дерева для элемента (по слою ArchiMate).
 * technology-physical объединяет Technology и Physical.
 */
function layerToFolderKey(layer: string | undefined): string | null {
	if (!layer) return null;
	switch (layer) {
		case 'Strategy':
			return 'strategy';
		case 'Business':
			return 'business';
		case 'Application':
			return 'application';
		case 'Technology':
		case 'Physical':
			return 'technology-physical';
		case 'Motivation':
			return 'motivation';
		case 'Implementation & Migration':
			return 'implementation-migration';
		case 'Other':
			return 'other';
		default:
			return null;
	}
}

function asList<T>(value: T | T[] | null | undefined): T[] {
	if (value == null) return [];
	return Array.isArray(value) ? value : [value];
}

/** Возвращает читаемое имя концепта. Не использует id — только name. */
function getConceptName(obj: {name?: string | {value?: string}; id?: string}): string {
	if (!obj) return '';
	const n = obj.name;
	if (typeof n === 'string' && n.trim()) return n.trim();
	if (n && typeof n === 'object' && typeof (n as {value?: string}).value === 'string') {
		const v = (n as {value: string}).value.trim();
		if (v) return v;
	}
	return '';
}

/** Ключи папок в порядке отображения */
export const FOLDER_ORDER = [
	'strategy',
	'business',
	'application',
	'technology-physical',
	'motivation',
	'implementation-migration',
	'other',
	'relations',
] as const;

/**
 * Извлекает элементы и связи из moddle-модели, сгруппированные по папкам.
 */
export function extractModelContent(
	model: {
		elementsNode?: {
			element?: Array<{
				id?: string;
				name?: string | {value?: string};
				type?: string;
				$type?: string;
			}>;
		};
		relationshipsNode?: {
			relationship?: Array<{
				id?: string;
				name?: string | {value?: string};
				type?: string;
				$type?: string;
				source?: {id?: string; name?: string | {value?: string}};
				target?: {id?: string; name?: string | {value?: string}};
			}>;
		};
	} | null
): ModelContent {
	const elementsByFolder: Record<string, ModelConcept[]> = {};
	for (const key of FOLDER_ORDER) {
		elementsByFolder[key] = [];
	}

	if (!model) return {elementsByFolder};

	const elements = asList(model.elementsNode?.element);
	const junctionTypes = new Set(['Junction', AND_JUNCTION, OR_JUNCTION]);
	for (const el of elements) {
		const type = el.type ?? getSemanticType(el as {$type?: string});
		let folderKey = layerToFolderKey(getLayerType(type));
		if (!folderKey && type && junctionTypes.has(type)) {
			folderKey = 'other';
		}
		if (folderKey && elementsByFolder[folderKey]) {
			elementsByFolder[folderKey].push({
				id: el.id ?? '',
				name: getConceptName(el),
				type: getTypeName(type) ?? type ?? 'Unknown',
			});
		}
	}

	const rels = asList(model.relationshipsNode?.relationship);
	for (const rel of rels) {
		const type = rel.type ?? getSemanticType(rel as {$type?: string});
		const typeName = getTypeName(type) ?? type ?? 'Relationship';
		const relName = getConceptName(rel);
		const sourceLabel = rel.source ? getConceptName(rel.source) : '';
		const targetLabel = rel.target ? getConceptName(rel.target) : '';
		const part = relName || typeName;
		const suffix = sourceLabel || targetLabel ? ` (${sourceLabel} - ${targetLabel})` : '';
		elementsByFolder.relations.push({
			id: rel.id ?? '',
			name: part + suffix,
			type: typeName,
		});
	}

	return {elementsByFolder};
}
